import { createContext, useContext, useEffect, useRef, type ComponentProps, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { locate } from '../nav/hubs';

/** Only navigation links resume a list; links carrying an explicit filter keep their target. */
function destination(to: string) {
  if (!to.startsWith('/') || to.startsWith('//')) return null;
  const url = new URL(to, 'https://control-center.invalid');
  const here = locate(url.pathname, url.search);
  if (!here?.exact) return null;
  return { url, tabKey: `${here.hub}/${here.tab}`, sub: url.searchParams.get('sub'), resolvedSub: here.sub };
}

function snapshot(pathname: string, search: string): string {
  const params = new URLSearchParams(search);
  // Opening a creation dialog is a one-time action, never a remembered list state.
  if (params.has('action')) {
    params.delete('action');
    params.delete('domain');
  }
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}`;
}

type Resume = (to: string) => string;
const NavigationContext = createContext<Resume | null>(null);

function resume(to: string, current: string, saved?: Map<string, string>): string {
  const target = destination(to);
  if (!target || [...target.url.searchParams.keys()].some((k) => k !== 'tab' && k !== 'sub')) return to;
  const active = destination(current);
  let remembered = active?.tabKey === target.tabKey && (!target.sub || active.resolvedSub === target.sub) ? current : undefined;
  remembered ??= saved?.get(target.sub ? `${target.tabKey}/${target.sub}` : target.tabKey);
  if (!remembered) return to;
  const hash = target.url.hash;
  return `${remembered}${hash}`;
}

/** In-memory navigation state, discarded with the authenticated shell on logout. */
export function NavigationMemoryProvider({ children }: { children: ReactNode }) {
  const { pathname, search } = useLocation();
  const saved = useRef(new Map<string, string>());
  const current = snapshot(pathname, search);
  useEffect(() => {
    const here = destination(current);
    if (!here) return;
    saved.current.set(here.tabKey, current);
    if (here.resolvedSub) saved.current.set(`${here.tabKey}/${here.resolvedSub}`, current);
  }, [current]);
  return <NavigationContext.Provider value={(to) => resume(to, current, saved.current)}>{children}</NavigationContext.Provider>;
}

export function useNavigationTarget(): Resume {
  const remembered = useContext(NavigationContext);
  const { pathname, search } = useLocation();
  return remembered ?? ((to) => resume(to, snapshot(pathname, search)));
}

/** Sidebar, tabs and breadcrumbs return to the list the operator was working with. */
export function NavigationLink({ to, ...props }: Omit<ComponentProps<typeof Link>, 'to'> & { to: string }) {
  const target = useNavigationTarget();
  return <Link to={target(to)} {...props} />;
}
